import { reduceErrors } from '../errors';
import { getSize } from '../iterable/getSize';

import { ENABLED, ERROR, REQUIRED, SELLING_METHOD, VALUE } from '../../constants/product';
import { FIND, OPERATIONS, REPLACE, MENU_ITEM } from '../../constants/bulkEdit';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '../../constants/channels';
import { ATTRIBUTE } from '../../constants/attributes';
import { VARIANTS } from '../../constants/variations';
import { EMPTY } from '../../constants';
import { FORM } from '../../constants/forms';

function about({ preview }) {
  return !preview.has('about');
}

function availability({ preview }) {
  return (
    !!preview.get(ERROR) || (
      !preview.hasIn([ATTRIBUTE.PREORDER, ATTRIBUTE.PREORDER]) &&
      !preview.hasIn([ATTRIBUTE.PREORDER, ATTRIBUTE.KEEP_ACTIVE]) &&
      !preview.hasIn([ATTRIBUTE.PREORDER, ATTRIBUTE.START_DATE]) &&
      !preview.hasIn([ATTRIBUTE.PREORDER, ATTRIBUTE.END_DATE]) &&
      !preview.hasIn([ATTRIBUTE.PREORDER, ATTRIBUTE.DEADLINE_DATE])
    )
  );
}

function barcode({ channel, operation, preview }) {
  return (
    !!preview.get(ERROR) ||
    operation === OPERATIONS[channel].BARCODE.FIND_AND_REPLACE
      ? !preview.getIn([ATTRIBUTE.BARCODE, FIND]) || !preview.getIn([ATTRIBUTE.BARCODE, REPLACE])
      : !preview.has(ATTRIBUTE.BARCODE)
  );
}

function description({ channel, operation, page, preview }) {
  switch (operation) {
    case OPERATIONS[channel].DESCRIPTION.AI_GENERATED: {
      return (
        getSize(preview.getIn([ATTRIBUTE.DESCRIPTION, 'products', 'processed', page])) === 0 ||
        getSize(preview.getIn([ATTRIBUTE.DESCRIPTION, 'products', 'processing', page])) > 0
      );
    }

    case OPERATIONS[channel].DESCRIPTION.FIND_AND_REPLACE: {
      return !preview.getIn([ATTRIBUTE.DESCRIPTION, FIND]) || !preview.getIn([ATTRIBUTE.DESCRIPTION, REPLACE]);
    }

    default: {
      return !preview.has(ATTRIBUTE.DESCRIPTION);
    }
  }
}

function category({ preview }) {
  return !preview.get(ATTRIBUTE.CATEGORY);
}

function collections({ preview }) {
  return !preview.has(ATTRIBUTE.COLLECTIONS);
}

function condition({ preview }) {
  return (
    !preview.hasIn([ATTRIBUTE.CONDITION, VALUE]) &&
    !preview.hasIn([ATTRIBUTE.CONDITION, ATTRIBUTE.DESCRIPTION])
  );
}

function countryCode({ preview }) {
  return !preview.getIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.COUNTRY_CODE]);
}

function files({ preview }) {
  return !preview.get(ATTRIBUTE.FILES);
}

function gtin({ channel, operation, preview }) {
  return (
    !!preview.get(ERROR) ||
    operation === OPERATIONS[channel].GTIN.FIND_AND_REPLACE
      ? !preview.getIn([ATTRIBUTE.GTIN, FIND]) || !preview.getIn([ATTRIBUTE.GTIN, REPLACE])
      : !preview.has(ATTRIBUTE.GTIN)
  );
}

function hsCode({ preview }) {
  return !preview.getIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.HS_CODE]);
}

function itemLocation({ preview }) {
  return (
    !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.COUNTRY]) &&
    !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.LOCATION]) &&
    !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.POSTAL_CODE])
  );
}

function itemCategory({ preview }) {
  return (
    preview.get(ERROR) ||
    !preview.get(ATTRIBUTE.ITEM_CATEGORY)
  );
}

function itemSpecifics({ preview, property }) {
  return !preview.has(property.get('name'));
}

function materials({ preview }) {
  return (
    !!preview.get(ERROR) ||
    !preview.get(ATTRIBUTE.MATERIALS)
  );
}

function metaDescription({ channel, operation, page, preview }) {
  switch (operation) {
    case OPERATIONS[channel].META_DESCRIPTION.AI_GENERATED: {
      return (
        getSize(preview.getIn([ATTRIBUTE.META_DESCRIPTION, 'products', 'processed', page])) === 0 ||
        getSize(preview.getIn([ATTRIBUTE.META_DESCRIPTION, 'products', 'processing', page])) > 0
      );
    }

    case OPERATIONS[channel].META_DESCRIPTION.FIND_AND_REPLACE: {
      return (
        !preview.getIn([ATTRIBUTE.META_DESCRIPTION, FIND]) ||
        !preview.getIn([ATTRIBUTE.META_DESCRIPTION, REPLACE])
      );
    }

    default: {
      return !preview.has(ATTRIBUTE.META_DESCRIPTION);
    }
  }
}

function pageTitle({ channel, operation, page, preview }) {
  switch (operation) {
    case OPERATIONS[channel].PAGE_TITLE.AI_GENERATED: {
      return (
        getSize(preview.getIn([ATTRIBUTE.PAGE_TITLE, 'products', 'processed', page])) === 0 ||
        getSize(preview.getIn([ATTRIBUTE.PAGE_TITLE, 'products', 'processing', page])) > 0
      );
    }

    case OPERATIONS[channel].PAGE_TITLE.FIND_AND_REPLACE: {
      return (
        !preview.getIn([ATTRIBUTE.PAGE_TITLE, FIND]) ||
        !preview.getIn([ATTRIBUTE.PAGE_TITLE, REPLACE])
      );
    }

    default: {
      return !preview.has(ATTRIBUTE.PAGE_TITLE);
    }
  }
}

function paymentPolicy({ preview }) {
  return !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.PAYMENT_POLICY]);
}

function personalization({ channel, preview }) {
  return (
    !!preview.getIn([ATTRIBUTE.PERSONALIZATION, ATTRIBUTE.CHAR_COUNT_MAX, ERROR]) ||
    !!preview.getIn([ATTRIBUTE.PERSONALIZATION, ATTRIBUTE.INSTRUCTIONS, ERROR]) || (
      preview.getIn([ATTRIBUTE.PERSONALIZATION, 'operation']) === OPERATIONS[channel].PERSONALIZATION.INSTRUCTIONS_FIND_AND_REPLACE && (
        !preview.getIn([ATTRIBUTE.PERSONALIZATION, ATTRIBUTE.INSTRUCTIONS, FIND]) ||
        !preview.getIn([ATTRIBUTE.PERSONALIZATION, ATTRIBUTE.INSTRUCTIONS, REPLACE])
      )
    ) || (
      !getSize(preview.getIn([ATTRIBUTE.PERSONALIZATION, ATTRIBUTE.INSTRUCTIONS])) &&
      !getSize(preview.getIn([ATTRIBUTE.PERSONALIZATION, ATTRIBUTE.CHAR_COUNT_MAX])) &&
      !preview.hasIn([ATTRIBUTE.PERSONALIZATION, REQUIRED]) &&
      !preview.hasIn([ATTRIBUTE.PERSONALIZATION, ENABLED])
    )
  );
}

function photos({ channel, operation, page, preview }) {
  switch (operation) {
    case OPERATIONS[channel].PHOTOS.ALT_TEXT: {
      return !preview.getIn([ATTRIBUTE.PHOTOS, VALUE]);
    }

    case OPERATIONS[channel].PHOTOS.ALT_TEXT_AI_GENERATED: {
      return (
        getSize(preview.getIn([ATTRIBUTE.PHOTOS, 'products', 'processed', page])) === 0 ||
        getSize(preview.getIn([ATTRIBUTE.PHOTOS, 'products', 'processing', page])) > 0
      );
    }

    default: {
      return !preview.hasIn([ATTRIBUTE.PHOTOS, 'operation']);
    }
  }
}

function price({ channel, preview }) {
  switch (channel) {
    case EBAY:
    case ETSY: {
      return (
        !!preview.get(ERROR) || (
          !preview.getIn([ATTRIBUTE.PRICE, VALUE, VALUE]) &&
          !preview.getIn([ATTRIBUTE.PRICE, VALUE, 'cents'])
        )
      );
    }

    case FAIRE: {
      return getSize(preview.getIn([ATTRIBUTE.PRICE, 'attributes'])) === 0 || (
        !preview.getIn([ATTRIBUTE.PRICE, 'value', 'value']) &&
        !preview.getIn([ATTRIBUTE.PRICE, 'value', 'cents'])
      );
    }

    case SHOPIFY: {
      return (
        !!preview.get(ERROR) || (
          !preview.getIn([ATTRIBUTE.PRICE, VALUE, VALUE]) &&
          !preview.getIn([ATTRIBUTE.PRICE, VALUE, 'cents']) &&
          !preview.getIn([ATTRIBUTE.PRICE, 'conversion']) &&
          !preview.getIn([ATTRIBUTE.CAP, 'conversion']) &&
          !preview.hasIn([ATTRIBUTE.PRICE, ATTRIBUTE.CHARGE_TAX])
        )
      );
    }

    default: {
      return true;
    }
  }
}

function processingProfile({ preview }) {
  return !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.PROCESSING_PROFILE]);
}

function productType({ preview }) {
  return !preview.has(ATTRIBUTE.PRODUCT_TYPE);
}

function productionPartners({ preview }) {
  return !preview.has(ATTRIBUTE.PRODUCTION_PARTNERS);
}

function quantity({ channel, preview }) {
  switch (channel) {
    case EBAY:
    case ETSY: {
      return !!preview.get(ERROR) || !preview.hasIn([ATTRIBUTE.QUANTITY, VALUE]);
    }

    case FAIRE: {
      return !!preview.get(ERROR) || (
        !preview.hasIn([ATTRIBUTE.QUANTITY, VALUE]) &&
        !preview.hasIn([ATTRIBUTE.QUANTITY, ATTRIBUTE.CONTINUE_SELLING]) &&
        !preview.hasIn([ATTRIBUTE.QUANTITY, ATTRIBUTE.RESTOCK_DATE])
      );
    }

    case SHOPIFY: {
      return (
        !!preview.get(ERROR) || (
          !preview.hasIn([ATTRIBUTE.QUANTITY, VALUE]) &&
          !preview.hasIn([ATTRIBUTE.QUANTITY, ATTRIBUTE.CONTINUE_SELLING]) &&
          !preview.hasIn([ATTRIBUTE.QUANTITY, ATTRIBUTE.TRACK_QUANTITY])
        )
      );
    }

    default: {
      return true;
    }
  }
}

function returnPolicy({ preview }) {
  return !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.RETURN_POLICY]);
}

function section({ preview }) {
  return !preview.has(ATTRIBUTE.SECTION);
}

function sellingMethod({ preview }) {
  const sellingMethodValue = preview.getIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.SELLING_METHOD, VALUE]);

  switch (sellingMethodValue) {
    case SELLING_METHOD.CASE: {
      return (
        !!preview.getIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.CASE_SIZE, ERROR]) ||
        !!preview.getIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.MINIMUM_ORDER_QUANTITY, ERROR])
      );
    }

    case SELLING_METHOD.ITEM: {
      return !!preview.getIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.MINIMUM_ORDER_QUANTITY, ERROR]);
    }

    case SELLING_METHOD.SIZE: {
      return !!preview.getIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.PER_STYLE_MINIMUM_ORDER_QUANTITY, ERROR]);
    }

    default: {
      return true;
    }
  }
}

function shippingDimensions({ channel, preview }) {
  switch (channel) {
    case EBAY: {
      return (
        getSize(preview.get(ERROR)) > 0 || (
          !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.DEPTH]) &&
          !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.LENGTH]) &&
          !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.WIDTH])
        )
      );
    }

    case ETSY: {
      return (
        getSize(preview.get(ERROR)) > 0 || (
          !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.HEIGHT]) &&
          !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.LENGTH]) &&
          !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.WIDTH])
        )
      );
    }

    case FAIRE: {
      return (
        getSize(preview.get(ERROR)) > 0 || (
          !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.HEIGHT]) &&
          !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.LENGTH]) &&
          !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.WIDTH]) &&
          !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.DISTANCE_UNIT])
        )
      );
    }

    default: {
      return true;
    }
  }
}

function shippingPolicy({ preview }) {
  return !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.SHIPPING_POLICY]);
}

function shippingProfile({ preview }) {
  return !preview.getIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.PROFILE_ID]);
}

function shippingWeight({ channel, preview }) {
  switch (channel) {
    case EBAY: {
      return (
        getSize(preview.get(ERROR)) > 0 || (
          !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.WEIGHT_MAJOR]) &&
          !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.WEIGHT_MINOR])
        )
      );
    }

    case ETSY: {
      return (
        !!preview.get(ERROR) ||
        !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.WEIGHT])
      );
    }

    case FAIRE: {
      return (
        !!preview.get(ERROR) || (
          !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.WEIGHT]) &&
          !preview.hasIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.MASS_UNIT])
        )
      );
    }

    default: {
      return true;
    }
  }
}

function sku({ channel, operation, preview }) {
  return (
    !!preview.get(ERROR) ||
    operation === OPERATIONS[channel].SKU.FIND_AND_REPLACE
      ? !preview.getIn([ATTRIBUTE.SKU, FIND]) || !preview.getIn([ATTRIBUTE.SKU, REPLACE])
      : !preview.has(ATTRIBUTE.SKU)
  );
}

function storeCategory({ preview }) {
  return !preview.has(ATTRIBUTE.STORE_CATEGORY);
}

function tags({ channel, operation, page, preview }) {
  switch (operation) {
    case OPERATIONS[channel].TAGS.AI_GENERATED: {
      return (
        getSize(preview.getIn([ATTRIBUTE.TAGS, 'products', 'processed', page])) === 0 ||
        getSize(preview.getIn([ATTRIBUTE.TAGS, 'products', 'processing', page])) > 0
      );
    }

    default: {
      return (
        preview.has(ERROR) || (
          !preview.hasIn([ATTRIBUTE.TAGS, 'profile']) &&
          !getSize(preview.getIn([ATTRIBUTE.TAGS, ATTRIBUTE.TAGS]))
        )
      );
    }
  }
}

function tariffCode({ preview }) {
  return !preview.getIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.TARIFF_CODE]);
}

function taxonomyAttributes({ attribute, preview }) {
  return !preview.has(attribute.get('property'));
}

function title({ channel, operation, page, preview }) {
  switch (operation) {
    case OPERATIONS[channel].TITLE.AI_GENERATED: {
      return (
        getSize(preview.getIn([ATTRIBUTE.TITLE, 'products', 'processed', page])) === 0 ||
        getSize(preview.getIn([ATTRIBUTE.TITLE, 'products', 'processing', page])) > 0
      );
    }

    case OPERATIONS[channel].TITLE.FIND_AND_REPLACE: {
      return !preview.getIn([ATTRIBUTE.TITLE, FIND]) || !preview.getIn([ATTRIBUTE.TITLE, REPLACE]);
    }

    default: {
      return !preview.has(ATTRIBUTE.TITLE);
    }
  }
}

function variations({ channel, preview }) {
  switch (channel) {
    case EBAY: {
      return (
        !!preview.getIn([ATTRIBUTE.VARIATIONS, FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, ERROR]) ||
        getSize(preview.getIn([ATTRIBUTE.VARIATIONS, FORM.VARIATIONS, VARIANTS])) === 0 ||
        reduceErrors(preview.getIn([ATTRIBUTE.VARIATIONS, FORM.VARIATIONS, ERROR]))
      );
    }

    case ETSY: {
      return getSize(preview.getIn([ATTRIBUTE.VARIATIONS, FORM.VARIATIONS, VARIANTS])) > 0
        ? reduceErrors(preview.getIn([ATTRIBUTE.VARIATIONS, FORM.VARIATIONS, ERROR]))
        : !preview.getIn([ATTRIBUTE.VARIATIONS, FORM.DETAILS, ATTRIBUTE.CATEGORY]);
    }

    case SHOPIFY: {
      return getSize(preview.getIn([ATTRIBUTE.VARIATIONS, FORM.VARIATIONS, VARIANTS])) > 0
        ? reduceErrors(preview.getIn([ATTRIBUTE.VARIATIONS, FORM.VARIATIONS, ERROR]))
        : !preview.getIn([ATTRIBUTE.VARIATIONS, FORM.DETAILS, ATTRIBUTE.CATEGORY]);
    }

    case FAIRE: {
      return (
        getSize(preview.getIn([ATTRIBUTE.VARIATIONS, FORM.VARIATIONS, VARIANTS])) === 0 ||
        reduceErrors(preview.getIn([ATTRIBUTE.VARIATIONS, FORM.VARIATIONS, ERROR]))
      );
    }

    default: {
      return true;
    }
  }
}

function vendor({ preview }) {
  return !preview.has(ATTRIBUTE.VENDOR);
}

function videos({ preview }) {
  return !preview.hasIn([ATTRIBUTE.VIDEOS, 'operation']);
}

function weight({ channel, preview }) {
  switch (channel) {
    case SHOPIFY: {
      return (
        !!preview.get(ERROR) || (
          !preview.getIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.WEIGHT]) &&
          !preview.getIn([ATTRIBUTE.SHIPPING, ATTRIBUTE.UNIT])
        )
      );
    }

    default: {
      return true;
    }
  }
}

export function isApplyDisabled(bulkEdit) {
  const channel = bulkEdit.get('channel');
  const preview = bulkEdit.get('preview', EMPTY.MAP);
  const products = bulkEdit.getIn(['products', 'ids']);
  const selectedMenuItem = bulkEdit.getIn(['menu', 'selected', 'item']);
  const operation = bulkEdit.getIn(['menu', 'operation']);
  const page = bulkEdit.getIn(['page', 'currentPage']);

  if (
    !products ||
    bulkEdit.get('processing') ||
    bulkEdit.get('saving') ||
    !getSize(bulkEdit.getIn(['products', 'selected']))
  ) {
    return true;
  }

  switch (selectedMenuItem) {
    case MENU_ITEM.ABOUT: {
      return about({ preview });
    }

    case MENU_ITEM.AVAILABILITY: {
      return availability({ preview });
    }

    case MENU_ITEM.BARCODE: {
      return barcode({ channel, operation, preview });
    }

    case MENU_ITEM.BODY_HTML:
    case MENU_ITEM.DESCRIPTION: {
      return description({ channel, operation, page, preview });
    }

    case MENU_ITEM.CAP:
    case MENU_ITEM.CPI:
    case MENU_ITEM.PRICE: {
      return price({ channel, preview });
    }

    case MENU_ITEM.CATEGORY: {
      return category({ preview });
    }

    case MENU_ITEM.COLLECTIONS: {
      return collections({ preview });
    }

    case MENU_ITEM.CONDITION: {
      return condition({ preview });
    }

    case MENU_ITEM.COUNTRY_CODE: {
      return countryCode({ preview });
    }

    case MENU_ITEM.FILES: {
      return files({ preview });
    }

    case MENU_ITEM.GTIN: {
      return gtin({ channel, operation, preview });
    }

    case MENU_ITEM.HS_CODE: {
      return hsCode({ preview });
    }

    case MENU_ITEM.ITEM_CATEGORY: {
      return itemCategory({ preview });
    }

    case MENU_ITEM.ITEM_SIZE: {
      return shippingDimensions({ channel, preview });
    }

    case MENU_ITEM.ITEM_WEIGHT: {
      return shippingWeight({ channel, preview });
    }

    case MENU_ITEM.ITEM_LOCATION: {
      return itemLocation({ preview });
    }

    case MENU_ITEM.MATERIALS: {
      return materials({ preview });
    }

    case MENU_ITEM.META_DESCRIPTION: {
      return metaDescription({ channel, operation, page, preview });
    }

    case MENU_ITEM.PAGE_TITLE: {
      return pageTitle({ channel, operation, page, preview });
    }

    case MENU_ITEM.PAYMENT_POLICY: {
      return paymentPolicy({ preview });
    }

    case MENU_ITEM.PERSONALIZATION: {
      return personalization({ channel, preview });
    }

    case MENU_ITEM.PHOTOS: {
      return photos({ channel, operation, page, preview });
    }

    case MENU_ITEM.PRODUCT_TYPE: {
      return productType({ preview });
    }

    case MENU_ITEM.PROCESSING_PROFILE: {
      return processingProfile({ preview });
    }

    case MENU_ITEM.PRODUCTION_PARTNER: {
      return productionPartners({ preview });
    }

    case MENU_ITEM.QUANTITY: {
      return quantity({ channel, preview });
    }

    case MENU_ITEM.RETURN_POLICY: {
      return returnPolicy({ preview });
    }

    case MENU_ITEM.SECTION: {
      return section({ preview });
    }

    case MENU_ITEM.SELLING_METHOD: {
      return sellingMethod({ preview });
    }

    case MENU_ITEM.SHIPPING_POLICY: {
      return shippingPolicy({ preview });
    }

    case MENU_ITEM.SHIPPING_PROFILE: {
      return shippingProfile({ preview });
    }

    case MENU_ITEM.SKU: {
      return sku({ channel, operation, preview });
    }

    case MENU_ITEM.STORE_CATEGORY: {
      return storeCategory({ preview });
    }

    case MENU_ITEM.TAGS: {
      return tags({ channel, operation, page, preview });
    }

    case MENU_ITEM.TARIFF_CODE: {
      return tariffCode({ preview });
    }

    case MENU_ITEM.TITLE: {
      return title({ channel, operation, page, preview });
    }

    case MENU_ITEM.VARIATIONS: {
      return variations({ channel, preview });
    }

    case MENU_ITEM.VENDOR: {
      return vendor({ preview });
    }

    case MENU_ITEM.VIDEOS: {
      return videos({ preview });
    }

    case MENU_ITEM.WEIGHT: {
      return weight({ channel, preview });
    }

    default: {
      if (bulkEdit.getIn(['menu', 'items', selectedMenuItem, 'isTaxonomy'])) {
        switch (channel) {
          case EBAY: {
            return itemSpecifics({
              preview,
              property: bulkEdit.getIn(['menu', 'items', selectedMenuItem]),
            });
          }

          case ETSY: {
            return taxonomyAttributes({
              attribute: bulkEdit.getIn(['menu', 'items', selectedMenuItem]),
              preview,
            });
          }

          default: {
            return true;
          }
        }
      }

      return true;
    }
  }
}
